<template>
  <div class="editor" v-if="editor">
    <editor-menu-bubble class="menububble" :editor="editor" @hide="hideLinkMenu" v-slot="{ commands, isActive, getMarkAttrs, menu }">
      <div class="menububble" :class="{ 'is-active': menu.isActive }" :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`">
        <form class="menububble__form" v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(commands.link, linkUrl)">
          <input class="menububble__input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
          <button class="menububble__button" @click="setLinkUrl(commands.link, null)" type="button">
            <svg class="bi ml-1" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#trash`"/>
            </svg>
          </button>
        </form>

        <template v-else>
          <button class="menububble__button" @click="showLinkMenu(getMarkAttrs('link'))" :class="{ 'is-active': isActive.link() }">
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link'}}</span>
            <svg class="bi ml-1" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#link`"/>
            </svg>
          </button>
        </template>
      </div>
    </editor-menu-bubble>

    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menubar">
        <div class="btn-group">
          <button type="button" class="btn btn-sm" title="Bold" :class="{ 'btn-dark': isActive.bold(), 'btn-outline-dark': !isActive.bold() }" @click="commands.bold">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#type-bold`"/>
            </svg>
          </button>
          <button type="button" class="btn btn-sm" title="Italic" :class="{ 'btn-dark': isActive.italic(), 'btn-outline-dark': !isActive.italic() }" @click="commands.italic">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#type-italic`"/>
            </svg>
          </button>
        </div>

        <div class="btn-group ml-2">
          <button type="button" class="btn btn-sm" title="Bullet list" :class="{ 'btn-dark': isActive.bullet_list(), 'btn-outline-dark': !isActive.bullet_list() }" @click="commands.bullet_list">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#list-ul`"/>
            </svg>
          </button>
          <button type="button" class="btn btn-sm" title="Ordered list" :class="{ 'btn-dark': isActive.ordered_list(), 'btn-outline-dark': !isActive.ordered_list() }" @click="commands.ordered_list">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#list-ol`"/>
            </svg>
          </button>
        </div>

        <div class="btn-group ml-2">
          <button type="button" class="btn btn-sm" title="Link" :class="{ 'btn-dark': isActive.link(), 'btn-outline-dark': !isActive.link() }" @click="commands.link">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#link`"/>
            </svg>
          </button>
        </div>

        <div class="btn-group ml-2">
          <button type="button" class="btn btn-sm btn-outline-dark" @click="commands.undo">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#arrow-90deg-left`"/>
            </svg>
          </button>
          <button type="button" class="btn btn-sm btn-outline-dark" @click="commands.redo">
            <svg class="bi" width="18" height="18" fill="currentColor">
              <use :xlink:href="`${bootstrapIcons}#arrow-90deg-right`"/>
            </svg>
          </button>
        </div>
      </div>
    </editor-menu-bar>

    <editor-content class="editor-content mt-2" :editor="editor"></editor-content>
  </div>
</template>

<script>
  import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from "tiptap"
  import {
    HardBreak,
    BulletList,
    OrderedList,
    ListItem,
    Bold,
    Italic,
    Link,
    History
  } from "tiptap-extensions"

  export default {
    name: "editor",
    components: { EditorContent, EditorMenuBar, EditorMenuBubble },
    props: {
      value: {
        type: String
      }
    },
    data() {
      return {
        bootstrapIcons: require("bootstrap-icons/bootstrap-icons.svg"),
        editor: null,
        linkUrl: null,
        linkMenuIsActive: false
      }
    },
    methods: {
      showLinkMenu(attrs) {
        this.linkUrl = attrs.href
        this.linkMenuIsActive = true
        this.$nextTick(() => {
          this.$refs.linkInput.focus()
        })
      },
      hideLinkMenu() {
        this.linkUrl = null
        this.linkMenuIsActive = false
      },
      setLinkUrl(command, url) {
        command({ href: url })
        this.hideLinkMenu()
      }
    },
    mounted() {
      this.editor = new Editor({
        extensions: [
          new HardBreak(),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new Bold(),
          new Italic(),
          new Link(),
          new History()
        ],
        content: this.value,
        onUpdate: ({ getHTML }) => {
          const state = getHTML()
          this.$emit("input", state)
        }
      })
    },
    beforeDestroy() {
      this.editor.destroy()
    }
  }
</script>

<style lang="scss" scoped>
  .editor-content {
    background-clip: padding-box;
    background-color: #fff;
    border-radius: 0.25rem;
    border: 1px solid #ced4da;
    color: #495057;
    display: block;
    font-size: 0.875rem;
    font-weight: 400;
    height: 150px;
    max-height: 400px;
    line-height: 1.5;
    padding: 0.375rem 0.75rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    width: 100%;
    overflow: auto;
  }

  .menububble {
    position: absolute;
    display: flex;
    z-index: 20;
    background: #000000;
    border-radius: 5px;
    padding: 0.3rem;
    margin-bottom: 0.5rem;
    transform: translateX(-50%);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s, visibility 0.2s;

    &.is-active {
      opacity: 1;
      visibility: visible;
    }

    &__button {
      display: inline-flex;
      background: transparent;
      border: 0;
      color: #FFFFFF;
      padding: 0.2rem 0.5rem;
      margin-right: 0.2rem;
      border-radius: 3px;
      cursor: pointer;
      align-items: center;

      &:last-child {
        margin-right: 0;
      }

      &:hover {
        background-color: rgba(#FFFFFF, 0.1);
      }

      &.is-active {
        background-color: rgba(#FFFFFF, 0.2);
      }
    }

    &__form {
      display: flex;
      align-items: center;
    }

    &__input {
      font: inherit;
      border: none;
      background: transparent;
      color: #FFFFFF;
    }
  }
</style>